import React, { useState } from 'react'
import { Table, Button, Row, Col, Form, Pagination } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Message from './../components/Message'
import Loader from './../components/Loader'
import { useGetBillsQuery, useDeleteBillMutation } from './../slices/billsApiSlice'
import { toast } from 'react-toastify'

const StaffBill = () => {
  // UI state
  const [filter, setFilter] = useState('')
  const [productSearch, setProductSearch] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  // Data fetching
  const { data, isLoading, error, refetch } = useGetBillsQuery(pageNumber)
  const [deleteBill, { isLoading: loadingDelete }] = useDeleteBillMutation()

  // Delete handler
  const deleteHandler = async (id) => {
    if (window.confirm('Delete this bill?')) {
      try {
        await deleteBill(id).unwrap()
        toast.success('Bill deleted')
        refetch()
      } catch (err) {
        toast.error(err?.data?.message || 'Delete failed')
      }
    }
  }

  // 1) Date‐range filter, 2) general filter (note/user/customer/product), 
  // 3) product‐only filter
  const filterBills = () => {
    if (!data?.bills) return []

    // 1) date range
    let result = data.bills.filter((bill) => {
      const d = new Date(bill.date)
      return (
        (!startDate || d >= new Date(startDate)) &&
        (!endDate   || d <= new Date(endDate))
      )
    })

    // 2) general text filter
    if (filter.trim()) {
      const lf = filter.toLowerCase()
      result = result.filter((bill) => {
        return (
          bill.note?.toLowerCase().includes(lf) ||
          bill.customerDetails?.company?.toLowerCase().includes(lf) ||
          bill.customerDetails?.name?.toLowerCase().includes(lf) ||
          bill.customerDetails?.address?.toLowerCase().includes(lf) ||
          bill.username?.toLowerCase().includes(lf) ||
          bill.billItems.some((it) => it.name.toLowerCase().includes(lf))
        )
      })
    }

    // 3) product‐only filter
    if (productSearch.trim()) {
      const lp = productSearch.toLowerCase()
      result = result
        .map((bill) => ({
          ...bill,
          billItems: bill.billItems.filter((it) =>
            it.name.toLowerCase().includes(lp)
          ),
        }))
        .filter((bill) => bill.billItems.length > 0)
    }

    return result
  }

  // Total calculation
  const totalBills = filterBills().reduce((sum, bill) => {
    return (
      sum +
      bill.billItems.reduce(
        (sub, it) => sub + it.qty * it.price,
        0
      )
    )
  }, 0)

  // Pagination handler
  const goPage = (p) => setPageNumber(p)

  return (
    <>
      <Row className="align-items-center mb-3">
        <Col><h1>Bills</h1></Col>
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter note/user/company/customer or any product"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>
        <Col md={6} className="text-end">
          <h4>Total Bills: D{totalBills.toFixed(2)}</h4>
        </Col>
      </Row>

      {/* New product‐only filter */}
      <Row className="mb-4">
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by product name only"
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
          />
        </Col>
      </Row>

      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || 'Error fetching bills'}
        </Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT</th>
                <th>QTY</th>
                <th>TOTAL</th>
                <th>GTOTAL</th>
                <th>COMPANY</th>
                <th>PATIENT</th>
                <th>SERIAL</th>
                <th>SUBCOM</th>
                <th>TEL</th>
                <th>NOTE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filterBills().map((bill) =>
                bill.billItems.map((item) => (
                  <tr key={`${bill._id}-${item.product}`}>
                    <td>
                      {new Date(bill.date).toLocaleDateString()}
                    </td>
                    <td>{item.name}</td>
                    <td>{item.qty}</td>
                    <td>
                      D{(item.qty * item.price).toFixed(0)}
                    </td>
                    <td>{(item.qty * item.price * 1.4).toFixed(0)}</td>
                    <td>{bill.customerDetails.company || 'N/A'}</td>
                    <td>
                      {bill.customerDetails.name || 'N/A'}
                    </td>
                    <td>
                      {bill.customerDetails.serialNumber || 'N/A'}
                    </td>
                    <td>
                      {bill.customerDetails.address || 'N/A'}
                    </td>
                    <td>
                      {bill.customerDetails.telephone || 'N/A'}
                    </td>
                    <td>{bill.note}</td>
                    <td  style={{display: "None"}}>
                      {item.images?.[0] && (
                        <img
                          src={item.images[0]}
                          alt=""
                          style={{
                            width: 30,
                            height: 30,
                            objectFit: 'cover',
                            borderRadius: 4,
                          }}
                        />
                      )}
                    </td>
                    <td>
                      <Button
                        as={Link}
                        to={`/bill/${bill._id}`}
                        variant="light"
                        size="sm"
                      >
                        Details
                      </Button>
                      <Button
                        style={{display: "None"}}
                        variant="danger"
                        size="sm"
                        className="ms-2"
                        onClick={() => deleteHandler(bill._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <Pagination>
            {[...Array(data.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => goPage(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  )
}

export default StaffBill